"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _styles = require("@mui/material/styles");
var _TextField = _interopRequireDefault(require("@mui/material/TextField"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireDefault(require("react"));
var _reactDom = _interopRequireDefault(require("react-dom"));
var _IconButton = _interopRequireDefault(require("./IconButton"));
var _TouchKeyboard = _interopRequireDefault(require("./TouchKeyboard.js"));
var _Keyboard = _interopRequireDefault(require("@mui/icons-material/Keyboard"));
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
const StyledTextField = (0, _styles.styled)(_TextField.default)(({
  theme
}) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: theme.palette.textfield?.main || theme.palette.background.default
  },
  "& .MuiOutlinedInput-root.Mui-focused": {
    backgroundColor: theme.palette.textfield?.focused || theme.palette.background.default
  }
}));

/**
 * Abstraction of Material UI's `TextField`
 */
class TextField extends _react.default.PureComponent {
  /**
   * Constructor.
   *
   * @param {Object} props
   * @return {self}
   */
  constructor(props) {
    super(props);

    /**
     * State
     *
     * @type {Object}
     */
    this.state = {
      /**
       * Are we focused?
       *
       * @type {Boolean}
       */
      focused: props.autoFocus || false,
      /**
       * Force enable the touch keyboard?
       * 
       * @type {Boolean}
       */
      forceTouchKeyboardEnabled: false
    };

    /**
     * Input ref
     *
     * @type {ReactRef}
     */
    this._inputRef = /*#__PURE__*/_react.default.createRef();

    /**
     * `window.scrollY` prior to focusing
     *
     * Used to reset the scroll position after a 
     * blur when the touch keyboard is enabled.
     * 
     * @type {Integer|null}
     */
    this._prefocusWindowScrollY = null;

    /**
     * Keyboard offset spacing component
     *
     * @type {DOMElement}
     */
    this._keyboardOffset = document.createElement("div");
  }

  /**
   * Component updated.
   *
   * @param {Object} prevProps
   * @return {void}
   */
  componentDidUpdate(prevProps) {
    if (prevProps.disabled !== this.props.disabled) {
      this.setState({
        forceTouchKeyboardEnabled: false
      });
    }
  }

  /**
   * Blurred.
   *
   * @return {void}
   */
  handleBlur = () => {
    this.setState({
      focused: false,
      forceTouchKeyboardEnabled: false
    });
    this.props.onBlur?.();
    if (this._prefocusWindowScrollY !== null) {
      window.scrollTo({
        top: this._prefocusWindowScrollY
      });
      this._prefocusWindowScrollY = null;
    }
    if (this._keyboardOffset.parentNode) {
      this._keyboardOffset.parentNode.removeChild(this._keyboardOffset);
    }
  };

  /**
   * Value changed.
   *
   * @param {Event} e
   * @return {void}
   */
  handleChange = e => {
    this.props.onChange(e.target.value || null, this.props.name);
  };

  /**
   * Focused.
   * 
   * @return {void}
   */
  handleFocus = () => {
    const input = this.inputRef?.current;
    this.setState({
      focused: true
    });
    this.props.onFocus?.();

    /**
     * We need to scroll into view when using the touch keyboard
     */
    if (input && this.isTouchKeyboardEnabled) {
      const {
        top,
        height
      } = input.getBoundingClientRect();
      const offset = this.constructor.touchKeyboardHeightOffset;
      if (top + height > window.innerHeight - offset) {
        if (!this.props.keyboardOffset) {
          const scroll = window.innerHeight - offset - height;
          this._prefocusWindowScrollY = window.scrollY;
          window.scrollTo({
            top: scroll
          });
        } else {
          // Material: FormControl > InputBase > Input (our `ref`)
          const offset = `${this.props.keyboardOffset}rem`;
          this._keyboardOffset.style.height = offset;
          input.parentNode?.parentNode?.appendChild?.(this._keyboardOffset);
          input.parentNode?.parentNode?.scrollIntoView?.();
        }
      }
    }
  };

  /**
   * Force enable the touch keyboard.
   *
   * @return {void}
   */
  handleForceEnableTouchKeyboard = () => {
    this.setState({
      forceTouchKeyboardEnabled: true
    });
    this.inputRef?.current?.focus?.();
  };

  /**
   * Touch keyboard input value changed.
   *
   * @param {String} value
   * @param {Integer} caret Caret position
   * @return {void}
   */
  handleTouchKeyboardValueChange = (value, caret) => {
    this.props.onChange?.(value, this.props.name);
    if (caret && this.inputRef?.current) {
      /**
       * `setSelectionRange()` doesn't work with `number` inputs
       * 
       * This is here to ensure the caret reliably remains at the 
       * expected position after touch keyboard inputs — otherwise 
       * it always resets to the end of the input after an input.
       *
       * For `number` inputs we'll just sacrifice this for now — 
       * the caret always resets to the end of the input, and there 
       * is **nothing** we can do about it. (The popular workaround 
       * of changing the input to `text`, then back to `number`, avoids 
       * the error but doesn't actually work to retain the caret in the 
       * intended position — it resets to the end when the input changes 
       * back to `type=number`.)
       */
      if (this.props.type !== "number") {
        this.inputRef?.current?.setSelectionRange?.(caret, caret);
      }
    }
  };

  /**
   * Render.
   * 
   * @return {ReactNode}
   */
  render() {
    return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_react.default.Fragment, {
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(StyledTextField, {
        ...this.props.TextFieldProps,
        autoComplete: this.props.autoComplete,
        autoFocus: this.props.autoFocus,
        className: this.props.className,
        color: this.props.color,
        disabled: this.props.disabled,
        error: this.props.error,
        fullWidth: this.props.fullWidth,
        helperText: this.props.error && this.props.errorText || this.props.helperText,
        inputRef: this.inputRef,
        InputProps: this.inputProps,
        InputLabelProps: this.InputLabelProps,
        label: this.props.label,
        multiline: this.props.multiline,
        onBlur: this.handleBlur,
        onChange: this.handleChange,
        onFocus: this.handleFocus,
        placeholder: this.props.placeholder || (this.props.placeholderLabel ? this.props.label : undefined),
        required: this.props.required,
        rows: this.props.rows
        // rowsMax={this.props.rowsMax}
        ,
        size: this.props.size,
        style: this.props.style,
        type: this.props.type,
        value: this.props.value || "",
        variant: this.props.variant
      }), this.shouldRenderTouchKeyboard && this.renderTouchKeyboard()]
    });
  }

  /**
   * Render the touch keyboard.
   * 
   * Portalling is used so the keyboard renders above any visible dialogs and popovers.
   * 
   * @return {ReactNode}
   */
  renderTouchKeyboard() {
    return /*#__PURE__*/_reactDom.default.createPortal(/*#__PURE__*/(0, _jsxRuntime.jsx)(_TouchKeyboard.default, {
      inputPattern: this.props.inputCharPattern ? new RegExp(this.props.inputCharPattern) : this.props.inputCharPatternMatchesPattern ? new RegExp(this.props.pattern) : undefined,
      maxLength: this.props.maxLength,
      onChange: this.handleTouchKeyboardValueChange,
      onEnter: this.props.onEnter,
      value: this.props.value
    }), document.body);
  }

  /**
   * Render the touch keyboard icon.
   * 
   * @return {ReactNode}
   */
  renderTouchKeyboardIcon() {
    return /*#__PURE__*/(0, _jsxRuntime.jsx)(_IconButton.default, {
      disabled: this.props.disabled,
      icon: _Keyboard.default,
      onClick: this.handleForceEnableTouchKeyboard,
      size: "small"
    });
  }

  /**
   * Get our `pattern` attribute value
   *
   * We apply a default pattern when we're a `number` input 
   * restricting the input to only digit characters.
   * 
   * @return {String|undefined}
   */
  get pattern() {
    if (this.props.pattern) return this.props.pattern;else if (this.props.type === "number") return "^[0-9]+$";else return undefined;
  }

  /**
   * Get our `inputProps`.
   * 
   * @return {Object}
   */
  get inputProps() {
    return {
      ...this.props.inputProps,
      endAdornment: this.shouldRenderTouchKeyboardIcon && this.renderTouchKeyboardIcon(),
      maxLength: this.props.maxLength,
      minLength: this.props.minLength,
      max: this.props.max,
      min: this.props.type === "number" && isNaN(this.props.min) ? 0 : this.props.min,
      pattern: this.pattern
    };
  }

  /**
   * Get our input's ref.
   *
   * @return {ReactRef}
   */
  get inputRef() {
    return this.props.inputRef || this._inputRef;
  }

  /**
   * Get our `InputLabelProps`.
   * 
   * @return {Object}
   */
  get InputLabelProps() {
    return {
      ...this.props.InputLabelProps,
      shrink: this.props.shrink || this.props.placeholder || this.props.placeholderLabel ? true : undefined
    };
  }

  /**
   * Get whether the touch keyboard is enabled.
   * 
   * @return {Boolean}
   */
  get isTouchKeyboardEnabled() {
    return this.props.touchKeyboard || this.state.forceTouchKeyboardEnabled;
  }

  /**
   * Get whether the touch keyboard icon is enabled.
   * 
   * @return {Boolean}
   */
  get isTouchKeyboardIconEnabled() {
    return this.props.touchKeyboardIcon;
  }

  /**
   * Get whether we should currently render the touch keyboard.
   * 
   * @return {Boolean}
   */
  get shouldRenderTouchKeyboard() {
    return (this.isTouchKeyboardEnabled && this.state.focused || this.state.forceTouchKeyboardEnabled) && !this.props.disabled;
  }

  /**
   * Get whether we should currently render the touch keyboard icon.
   * 
   * @return {Boolean}
   */
  get shouldRenderTouchKeyboardIcon() {
    return this.isTouchKeyboardIconEnabled && !this.props.disabled;
  }

  /**
   * Touch keyboard height
   *
   * This is used as an offset when scrolling into view with touch keyboard.
   *
   * The value should match the pixel height of the touch keyboard with an offset.
   * 
   * @type {Integer}
   */
  static touchKeyboardHeightOffset = 240;
  static propTypes = {
    InputProps: _propTypes.default.object,
    InputLabelProps: _propTypes.default.object,
    TextFieldProps: _propTypes.default.object,
    autoComplete: _propTypes.default.string,
    // https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#autofill
    autoFocus: _propTypes.default.bool,
    className: _propTypes.default.string,
    color: _propTypes.default.oneOf(["primary", "secondary"]),
    disabled: _propTypes.default.bool,
    error: _propTypes.default.bool,
    errorText: _propTypes.default.string,
    fullWidth: _propTypes.default.bool,
    helperText: _propTypes.default.string,
    inputProps: _propTypes.default.object,
    inputRef: _propTypes.default.oneOfType([_propTypes.default.func, _propTypes.default.shape({
      current: _propTypes.default.instanceOf(Element)
    })]),
    keyboardOffset: _propTypes.default.number,
    label: _propTypes.default.string,
    max: _propTypes.default.number,
    maxLength: _propTypes.default.number,
    min: _propTypes.default.number,
    minLength: _propTypes.default.number,
    multiline: _propTypes.default.bool,
    name: _propTypes.default.string,
    onBlur: _propTypes.default.func,
    onChange: _propTypes.default.func,
    onFocus: _propTypes.default.func,
    pattern: _propTypes.default.string,
    placeholder: _propTypes.default.string,
    placeholderLabel: _propTypes.default.bool,
    required: _propTypes.default.bool,
    rows: _propTypes.default.number,
    rowsMax: _propTypes.default.number,
    shrink: _propTypes.default.bool,
    size: _propTypes.default.oneOf(["small", "medium"]),
    style: _propTypes.default.object,
    touchKeyboard: _propTypes.default.bool,
    touchKeyboardIcon: _propTypes.default.bool,
    type: _propTypes.default.string,
    value: _propTypes.default.string,
    variant: _propTypes.default.oneOf(["standard", "outlined", "filled"])
  };
  static defaultProps = {
    color: "primary",
    size: "small",
    touchKeyboardIcon: true,
    variant: "outlined"
  };
}
var _default = exports.default = TextField;